import React, { useEffect, useRef, useState } from 'react';
import Layout from '../Layout/Layout.js';
import DataTable from 'react-data-table-component';
import { Button, Form } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import { APIUrl } from '../../Helper/APIUrl.js';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link'
];

const parseJSON = async (response) => {
  try {
    if (response.status === 204 || response.status === 205) {
      return null;
    }

    const text = await response.text();
    if (!text) {
      return null;
    }

    return JSON.parse(text);
  }
  catch (error) {
    console.warn('Failed to parse JSON response', error);
    return null;
  }
};

const getPlainTextFromHtml = (html) => {
  if (!html) {
    return '';
  }

  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
};

function AddPolicies() {
  const token = JSON.parse(localStorage.getItem('tcn'));
  const localStorageUserId = JSON.parse(localStorage.getItem('userid'));
  const authToken = typeof token === 'string' ? token : token?.token || token?.Token || '';

  const { enqueueSnackbar } = useSnackbar();
  const { quill, quillRef } = useQuill({ modules: quillModules, formats: quillFormats });

  const [search, setSearch] = useState('');
  const [policiesList, setPoliciesList] = useState([]);
  const [filteredPoliciesList, setFilteredPoliciesList] = useState([]);
  const [typeList, setTypeList] = useState([]);

  const [policiesId, setPoliciesId] = useState(0);
  const [typeId, setTypeId] = useState(0);
  const [policyContent, setPolicyContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [createdBy] = useState(localStorageUserId || 0);
  const [createdOn, setCreatedOn] = useState('');
  const [modifyBy] = useState(localStorageUserId || 0);
  const [modifiedOn, setModifiedOn] = useState('');
  const [edit, setEdit] = useState(false);

  const typeInputRef = useRef(null);

  const resetForm = () => {
    setPoliciesId(0);
    setTypeId(0);
    setPolicyContent('');
    setIsActive(true);
    setCreatedOn('');
    setModifiedOn('');
    setEdit(false);

    if (quill) {
      quill.setText('');
    }
  };

  const getPoliciesTypes = async () => {
    try {
      const response = await fetch(APIUrl.SZType + 'GetSZTypeBySectionId/39', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({})
      });

      const result = await parseJSON(response);
      const data = Array.isArray(result?.data) ? result.data : [];
      const activeTypes = data.filter((item) => item.IsActive !== false);
      setTypeList(activeTypes.sort((a, b) => (b.TypeId || 0) - (a.TypeId || 0)));
    }
    catch (error) {
      console.log(error);
      setTypeList([]);
    }
  };

  const getAllPolicies = async () => {
    try {
      const response = await fetch(APIUrl.Policies + 'GetAllPolicies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({})
      });

      const result = await parseJSON(response);
      const data = Array.isArray(result?.data) ? result.data : [];
      const sorted = [...data].sort((a, b) => (b.PoliciesId || 0) - (a.PoliciesId || 0));
      setPoliciesList(sorted);
      setFilteredPoliciesList(sorted);
    }
    catch (error) {
      console.log(error);
      setPoliciesList([]);
      setFilteredPoliciesList([]);
    }
  };

  useEffect(() => {
    getPoliciesTypes();
    getAllPolicies();
  }, []);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const handleChange = () => {
      const currentHtml = quill.root.innerHTML;
      setPolicyContent(currentHtml === '<p><br></p>' ? '' : currentHtml);
    };

    quill.on('text-change', handleChange);

    return () => {
      quill.off('text-change', handleChange);
    };
  }, [quill]);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const nextHtml = policyContent || '';
    if (quill.root.innerHTML !== nextHtml) {
      quill.root.innerHTML = nextHtml;
    }
  }, [policyContent, quill]);

  useEffect(() => {
    if (!typeId || Number(typeId) === 0) {
      setPolicyContent('');
      if (quill) {
        quill.setText('');
      }
      return;
    }

    const existingPolicy = policiesList.find((item) => Number(item.TypeId) === Number(typeId));
    if (existingPolicy) {
      // Auto-load full record when existing policy found
      setPoliciesId(existingPolicy.PoliciesId || 0);
      setPolicyContent(existingPolicy.PolicyContent || '');
      setIsActive(existingPolicy.IsActive ?? true);
      setCreatedOn(existingPolicy.CreatedOn || '');
      setModifiedOn(existingPolicy.ModifiedOn || '');
      setEdit(true);
    } else {
      // Clear for new policy
      setPoliciesId(0);
      setPolicyContent('');
      setIsActive(true);
      setCreatedOn('');
      setModifiedOn('');
      setEdit(false);
      if (quill) {
        quill.setText('');
      }
    }
  }, [typeId, policiesList, quill]);

  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      setFilteredPoliciesList(policiesList);
      return;
    }

    const result = policiesList.filter((item) => {
      const typeName = item.PolicyType || item.TypeName || '';
      const contentText = getPlainTextFromHtml(item.PolicyContent || '');

      return (
        typeName.toLowerCase().includes(term) ||
        contentText.toLowerCase().includes(term)
      );
    });

    setFilteredPoliciesList(result);
  }, [search, policiesList]);

  const handleEditPolicies = (row) => {
    const policiesRecord = row || {};

    setPoliciesId(policiesRecord.PoliciesId || 0);
    setTypeId(policiesRecord.TypeId || 0);
    setPolicyContent(policiesRecord.PolicyContent || '');
    setIsActive(policiesRecord.IsActive ?? true);
    setCreatedOn(policiesRecord.CreatedOn || '');
    setModifiedOn(policiesRecord.ModifiedOn || '');
    setEdit(true);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    typeInputRef.current?.focus();
  };

  const buildPayload = () => {
    const now = new Date().toISOString();
    const parsedTypeId = parseInt(typeId, 10) || 0;
    const parsedPoliciesId = parseInt(policiesId, 10) || 0;
    const parsedCreatedBy = parseInt(createdBy, 10) || 0;
    const parsedModifyBy = parseInt(modifyBy, 10) || 0;

    return {
      PoliciesId: parsedPoliciesId,
      TypeId: parsedTypeId,
      PolicyContent: policyContent || '',
      IsActive: Boolean(isActive),
      CreatedBy: parsedCreatedBy,
      CreatedOn: createdOn || now,
      ModifyBy: parsedModifyBy,
      ModifiedOn: now
    };
  };

  const validatePolicies = () => {
    if (!authToken) {
      enqueueSnackbar('Please login first.', { variant: 'warning' });
      return false;
    }

    if (!typeId || Number(typeId) === 0) {
      enqueueSnackbar('Select Policy Type.', { variant: 'warning' });
      return false;
    }

    if (!getPlainTextFromHtml(policyContent)) {
      enqueueSnackbar('Enter Policy Content.', { variant: 'warning' });
      quill?.focus();
      return false;
    }

    return true;
  };

  const insertPolicies = async () => {
    if (!validatePolicies()) {
      return;
    }

    try {
      const payload = buildPayload();
      const response = await fetch(APIUrl.Policies + 'UpdatePolicies/'+policiesId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJSON(response);
      if (result?.Message === 'Success' || result?.returnID !== undefined) {
        enqueueSnackbar(result?.Message || 'Successfully Added', { variant: 'success' });
        await getAllPolicies();
        resetForm();
        return;
      }

      enqueueSnackbar(result?.Message || 'Failed to save policy.', { variant: 'error' });
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar(error?.message || 'Error while saving policy.', { variant: 'error' });
    }
  };

  const updatePolicies = async () => {
    if (!validatePolicies()) {
      return;
    }

    if (!policiesId) {
      enqueueSnackbar('Policy id is missing for update.', { variant: 'warning' });
      return;
    }

    try {
      const payload = buildPayload();
      const response = await fetch(APIUrl.Policies + 'UpdatePolicies/' + policiesId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      const result = await parseJSON(response);
      if (result?.Message === 'Success' || result?.returnID !== undefined) {
        enqueueSnackbar(result?.Message || 'Successfully Updated', { variant: 'success' });
        await getAllPolicies();
        resetForm();
        return;
      }

      enqueueSnackbar(result?.Message || 'Failed to update policy.', { variant: 'error' });
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar(error?.message || 'Error while updating policy.', { variant: 'error' });
    }
  };

  const activateDeactivatePolicies = async (id, valueText) => {
    try {
      const response = await fetch(APIUrl.Policies + 'ActivateDeactivatePolicies/' + id, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ Value: valueText })
      });

      const result = await parseJSON(response);
      if (result) {
        await getAllPolicies();
        enqueueSnackbar(valueText === 'activate' ? 'Successfully Activated' : 'Successfully Deactivated', {
          variant: valueText === 'activate' ? 'success' : 'warning'
        });
      }
    }
    catch (error) {
      console.log(error);
      enqueueSnackbar('Failed to update policy status.', { variant: 'error' });
    }
  };

  const columns = [
    {
      name: 'SNo.',
      selector: (row, index) => index + 1,
      width: '80px'
    },
    {
      name: 'Type',
      selector: (row) => row.PolicyType || row.TypeName || row.TypeId,
      sortable: true,
      grow: 1
    },
    {
      name: 'Content',
      selector: (row) => getPlainTextFromHtml(row.PolicyContent || '').slice(0, 120),
      grow: 3
    },
    {
      name: 'Status',
      selector: (row) => row.IsActive
        ? <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Activated</label>
        : <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Deactivated</label>
    },
    {
      name: 'Action',
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => handleEditPolicies(row)}
            className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Button>
          {
            row.IsActive
              ?
              <Button
                type="button"
                onClick={() => activateDeactivatePolicies(row.PoliciesId, 'deactivate')}
                className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-4 py-2"
              >
                <i className="fa fa-toggle-on" aria-hidden="true"></i>
              </Button>
              :
              <Button
                type="button"
                onClick={() => activateDeactivatePolicies(row.PoliciesId, 'activate')}
                className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-4 py-2"
              >
                <i className="fa fa-toggle-off" aria-hidden="true"></i>
              </Button>
          }
        </div>
      )
    }
  ];

  return (
    <div>
      <Layout>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Add Policies</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Policy Type</label>
                  <Form.Select
                    ref={typeInputRef}
                    value={typeId}
                    onChange={(e) => setTypeId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                  >
                    <option value="0">Select Policy Type</option>
                    {
                      typeList.map((item) => (
                        <option key={item.TypeId} value={item.TypeId}>
                          {item.TypeName || item.Value || item.TypeId}
                        </option>
                      ))
                    }
                  </Form.Select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Policy Content</label>
                  <div className="overflow-hidden rounded-lg border border-gray-300 bg-white">
                    <div ref={quillRef} className="min-h-[220px] bg-white" />
                  </div>
                </div>

              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-3">
                {
                  edit ? (
                    <Button
                      type="button"
                      onClick={updatePolicies}
                      className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      Update
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={insertPolicies}
                      className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      Add or Update
                    </Button>
                  )
                }
                {/* <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Reset
                </button> */}
              </div>
            </div>

            {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                  <h2 className="text-xl font-semibold text-gray-900">All Policies</h2>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    type="text"
                    placeholder="Search by type or content"
                    className="w-full sm:w-96 rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <DataTable
                  columns={columns}
                  data={filteredPoliciesList}
                  pagination
                  fixedHeader
                  fixedHeaderScrollHeight="550px"
                  selectableRows
                  selectableRowsHighlight
                  highlightOnHover
                />
              </div>
            </div> */}
          </div>
        </main>
      </Layout>
    </div>
  )
}

export default AddPolicies;
